/**
 * Songs Data Access Layer
 * Typed functions for songs CRUD operations using Supabase
 */

import { supabase } from '../supabaseClient';
import type { Database } from '../database.types';

type Song = Database['public']['Tables']['songs']['Row'];
type SongInsert = Database['public']['Tables']['songs']['Insert'];
type SongUpdate = Database['public']['Tables']['songs']['Update'];

export interface SongPayload {
  title: string;
  artist: string;
  slug: string;
  description?: string; // card_caption for Explore cards
  lyric_spotlight?: string; // lyric_hook
  story_body?: string;
  spotify_url?: string;
  apple_music_url?: string;
  youtube_url?: string;
  image_url?: string; // cover_art_path
  status?: 'draft' | 'published' | 'archived';
  country?: string;
  region?: string;
  tags?: string[];
  themes?: string[];
  deep_insight?: string;
  quick_fact?: string;
  context_in_time?: string;
  related_song_ids?: string[];
  meta_title?: string;
  meta_description?: string;
}

export interface SongListItem {
  id: string;
  title: string;
  artist: string;
  slug: string;
  status: string;
  image_url: string | null;
  updated_at: string | null;
  published_at: string | null;
  tags: string[] | null;
  themes: string[] | null;
}

export interface SongDetail extends Song {
  // Include full song details
}

/**
 * List all songs (admin view)
 */
export async function listSongs(options?: {
  status?: 'draft' | 'published' | 'archived';
  search?: string;
  includeDrafts?: boolean;
}): Promise<{ data: SongListItem[] | null; error: any }> {
  try {
    let query = supabase
      .from('songs')
      .select('id, title, artist, slug, status, image_url, updated_at, published_at, tags, themes')
      .order('updated_at', { ascending: false });

    if (options?.status) {
      query = query.eq('status', options.status);
    }

    if (options?.search) {
      query = query.or(`title.ilike.%${options.search}%,artist.ilike.%${options.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error listing songs:', error);
      return { data: null, error };
    }

    return { data: data as SongListItem[], error: null };
  } catch (err) {
    console.error('Unexpected error listing songs:', err);
    return { data: null, error: err };
  }
}

/**
 * Get a single song by slug
 */
export async function getSongBySlug(slug: string): Promise<{ data: SongDetail | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) {
      console.error('Error fetching song by slug:', error);
      return { data: null, error };
    }

    return { data: data as SongDetail | null, error: null };
  } catch (err) {
    console.error('Unexpected error fetching song:', err);
    return { data: null, error: err };
  }
}

/**
 * Get a single song by ID
 */
export async function getSongById(id: string): Promise<{ data: SongDetail | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching song by ID:', error);
      return { data: null, error };
    }

    return { data: data as SongDetail | null, error: null };
  } catch (err) {
    console.error('Unexpected error fetching song:', err);
    return { data: null, error: err };
  }
}

/**
 * Create a new song
 */
export async function createSong(
  payload: SongPayload,
  userId: string
): Promise<{ data: Song | null; error: any }> {
  try {
    const insertData: SongInsert = {
      title: payload.title,
      artist: payload.artist,
      slug: payload.slug,
      description: payload.description || null,
      lyric_spotlight: payload.lyric_spotlight || null,
      story_body: payload.story_body || null,
      spotify_url: payload.spotify_url || null,
      apple_music_url: payload.apple_music_url || null,
      youtube_url: payload.youtube_url || null,
      image_url: payload.image_url || null,
      status: payload.status || 'draft',
      country: payload.country || null,
      region: payload.region || null,
      tags: payload.tags || [],
      themes: payload.themes || [],
      deep_insight: payload.deep_insight || null,
      quick_fact: payload.quick_fact || null,
      context_in_time: payload.context_in_time || null,
      related_song_ids: payload.related_song_ids || [],
      meta_title: payload.meta_title || null,
      meta_description: payload.meta_description || null,
      created_by: userId,
      last_updated_by: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('songs')
      .insert(insertData)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error creating song:', error);
      return { data: null, error };
    }

    if (!data) {
      console.error('Failed to create song - no data returned');
      return { data: null, error: new Error('Failed to create song') };
    }

    return { data: data as Song, error: null };
  } catch (err) {
    console.error('Unexpected error creating song:', err);
    return { data: null, error: err };
  }
}

/**
 * Update an existing song
 */
export async function updateSong(
  id: string,
  payload: Partial<SongPayload>,
  userId: string
): Promise<{ data: Song | null; error: any }> {
  try {
    const updateData: SongUpdate = {
      ...(payload.title && { title: payload.title }),
      ...(payload.artist && { artist: payload.artist }),
      ...(payload.slug && { slug: payload.slug }),
      ...(payload.description !== undefined && { description: payload.description }),
      ...(payload.lyric_spotlight !== undefined && { lyric_spotlight: payload.lyric_spotlight }),
      ...(payload.story_body !== undefined && { story_body: payload.story_body }),
      ...(payload.spotify_url !== undefined && { spotify_url: payload.spotify_url }),
      ...(payload.apple_music_url !== undefined && { apple_music_url: payload.apple_music_url }),
      ...(payload.youtube_url !== undefined && { youtube_url: payload.youtube_url }),
      ...(payload.image_url !== undefined && { image_url: payload.image_url }),
      ...(payload.status && { status: payload.status }),
      ...(payload.country !== undefined && { country: payload.country }),
      ...(payload.region !== undefined && { region: payload.region }),
      ...(payload.tags !== undefined && { tags: payload.tags }),
      ...(payload.themes !== undefined && { themes: payload.themes }),
      ...(payload.deep_insight !== undefined && { deep_insight: payload.deep_insight }),
      ...(payload.quick_fact !== undefined && { quick_fact: payload.quick_fact }),
      ...(payload.context_in_time !== undefined && { context_in_time: payload.context_in_time }),
      ...(payload.related_song_ids !== undefined && { related_song_ids: payload.related_song_ids }),
      ...(payload.meta_title !== undefined && { meta_title: payload.meta_title }),
      ...(payload.meta_description !== undefined && { meta_description: payload.meta_description }),
      ...(payload.draft_secondary_tags !== undefined && { draft_secondary_tags: payload.draft_secondary_tags }),
      last_updated_by: userId,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('songs')
      .update(updateData)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error updating song:', error);
      return { data: null, error };
    }

    if (!data) {
      console.error('Song not found or update not allowed');
      return { data: null, error: new Error('Song not found or update not allowed') };
    }

    return { data: data as Song, error: null };
  } catch (err) {
    console.error('Unexpected error updating song:', err);
    return { data: null, error: err };
  }
}

/**
 * Create a new draft song with minimal data
 */
export async function createSongDraft(
  title: string,
  artist: string,
  userId: string
): Promise<{ data: Song | null; error: any }> {
  const slug = generateSlug(title, artist);

  return createSong({
    title,
    artist,
    slug,
    status: 'draft',
  }, userId);
}

/**
 * Check if a slug already exists
 */
export async function slugExists(
  slug: string,
  excludeId?: string
): Promise<boolean> {
  try {
    let query = supabase
      .from('songs')
      .select('id')
      .eq('slug', slug);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error checking slug:', error);
      return false;
    }

    return !!data;
  } catch (err) {
    console.error('Unexpected error checking slug:', err);
    return false;
  }
}

/**
 * Generate a URL-safe slug from title and artist
 */
export function generateSlug(title: string, artist: string): string {
  const combined = `${artist}-${title}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return combined;
}

/**
 * Set song status (generic)
 */
export async function setSongStatus(
  id: string,
  status: 'draft' | 'published' | 'archived',
  userId: string
): Promise<{ data: Song | null; error: any }> {
  try {
    const updateData: any = {
      status,
      last_updated_by: userId,
      updated_at: new Date().toISOString(),
    };

    if (status === 'published') {
      updateData.published_at = new Date().toISOString();
    }

    if (status === 'archived') {
      updateData.archived_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('songs')
      .update(updateData)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error setting song status:', error);
      return { data: null, error };
    }

    if (!data) {
      console.error('Song not found or status update not allowed');
      return { data: null, error: new Error('Song not found or status update not allowed') };
    }

    return { data: data as Song, error: null };
  } catch (err) {
    console.error('Unexpected error setting song status:', err);
    return { data: null, error: err };
  }
}

/**
 * Publish a song
 */
export async function publishSong(
  id: string,
  userId: string
): Promise<{ data: Song | null; error: any }> {
  return setSongStatus(id, 'published', userId);
}

/**
 * Get primary tag IDs for a song
 */
export async function getSongPrimaryTags(songId: string): Promise<{ data: string[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('song_primary_tag_map')
      .select('primary_tag_id')
      .eq('song_id', songId);

    if (error) {
      console.error('Error fetching song primary tags:', error);
      return { data: null, error };
    }

    return { data: data.map(row => row.primary_tag_id), error: null };
  } catch (err) {
    console.error('Unexpected error fetching song primary tags:', err);
    return { data: null, error: err };
  }
}

/**
 * Update song primary tags (replaces all)
 */
export async function updateSongPrimaryTags(songId: string, tagIds: string[]): Promise<{ error: any }> {
  try {
    // Delete existing mappings
    const { error: deleteError } = await supabase
      .from('song_primary_tag_map')
      .delete()
      .eq('song_id', songId);

    if (deleteError) {
      console.error('Error deleting song primary tags:', deleteError);
      return { error: deleteError };
    }

    // Insert new mappings
    if (tagIds.length > 0) {
      const inserts = tagIds.map(tagId => ({ song_id: songId, primary_tag_id: tagId }));
      const { error: insertError } = await supabase
        .from('song_primary_tag_map')
        .insert(inserts);

      if (insertError) {
        console.error('Error inserting song primary tags:', insertError);
        return { error: insertError };
      }
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error updating song primary tags:', err);
    return { error: err };
  }
}

/**
 * Update song secondary tags (replaces all mappings by tag name)
 */
export async function updateSongSecondaryTags(songId: string, tagNames: string[]): Promise<{ error: any }> {
  try {
    // Delete existing mappings
    const { error: deleteError } = await supabase
      .from('song_tag_map')
      .delete()
      .eq('song_id', songId);

    if (deleteError) {
      console.error('Error deleting song secondary tags:', deleteError);
      return { error: deleteError };
    }

    // Re-attach by name (creates tags if needed)
    if (tagNames.length > 0) {
      const { error: attachError } = await attachSecondaryTagsByName(songId, tagNames);
      if (attachError) return { error: attachError };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error updating song secondary tags:', err);
    return { error: err };
  }
}

/**
 * Delete a song (permanently removes from database)
 */
export async function deleteSong(id: string): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('songs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting song:', error);
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error deleting song:', err);
    return { error: err };
  }
}

/**
 * Generate a unique slug by appending numbers if needed
 */
export async function generateUniqueSlug(
  title: string,
  artist: string,
  excludeId?: string
): Promise<string> {
  let baseSlug = generateSlug(title, artist);
  let slug = baseSlug;
  let counter = 2;

  while (await slugExists(slug, excludeId)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }

  return slug;
}

/**
 * Unpublish a song (revert to draft)
 */
export async function unpublishSong(
  id: string,
  userId: string
): Promise<{ data: Song | null; error: any }> {
  return setSongStatus(id, 'draft', userId);
}

/**
 * Archive a song
 */
export async function archiveSong(
  id: string,
  userId: string
): Promise<{ data: Song | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('songs')
      .update({
        status: 'archived',
        archived_at: new Date().toISOString(),
        last_updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error archiving song:', error);
      return { data: null, error };
    }

    if (!data) {
      console.error('Song not found or archive not allowed');
      return { data: null, error: new Error('Song not found or archive not allowed') };
    }

    return { data: data as Song, error: null };
  } catch (err) {
    console.error('Unexpected error archiving song:', err);
    return { data: null, error: err };
  }
}

/**
 * List all tags
 */
export async function listTags(): Promise<{ data: any[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('song_tags')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error listing tags:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error listing tags:', err);
    return { data: null, error: err };
  }
}

/**
 * List all themes
 */
export async function listThemes(): Promise<{ data: any[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('song_themes')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error listing themes:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Unexpected error listing themes:', err);
    return { data: null, error: err };
  }
}

/**
 * Upload cover art to Supabase Storage
 */
export async function uploadCoverArt(
  file: File,
  songSlug: string
): Promise<{ data: { path: string } | null; error: any }> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${songSlug}-${Date.now()}.${fileExt}`;
    const filePath = `covers/${fileName}`;

    const { data, error } = await supabase.storage
      .from('album-art')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      console.error('Error uploading cover art:', error);
      return { data: null, error };
    }

    const { data: urlData } = supabase.storage
      .from('album-art')
      .getPublicUrl(filePath);

    return { data: { path: urlData.publicUrl }, error: null };
  } catch (err) {
    console.error('Unexpected error uploading cover art:', err);
    return { data: null, error: err };
  }
}

// =============================================
// SECONDARY TAGS (song_tags)
// =============================================

export interface SecondaryTag {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  usage_count?: number;
}

/**
 * Search secondary tags by name with usage count
 */
export async function searchSecondaryTags(query: string): Promise<{ data: SecondaryTag[] | null; error: any }> {
  try {
    const { data: tags, error } = await supabase
      .from('song_tags')
      .select('id, name, slug, description')
      .ilike('name', `%${query}%`)
      .order('name')
      .limit(20);

    if (error) {
      console.error('Error searching secondary tags:', error);
      return { data: null, error };
    }

    // Get usage counts for these tags
    const tagIds = tags?.map(t => t.id) || [];
    if (tagIds.length === 0) {
      return { data: [], error: null };
    }

    const { data: usageCounts, error: usageError } = await supabase
      .from('song_tag_map')
      .select('tag_id')
      .in('tag_id', tagIds);

    if (usageError) {
      console.error('Error fetching usage counts:', usageError);
      // Return tags without usage counts if this fails
      return { data: tags as SecondaryTag[], error: null };
    }

    // Count occurrences
    const countMap: Record<string, number> = {};
    usageCounts?.forEach(row => {
      countMap[row.tag_id] = (countMap[row.tag_id] || 0) + 1;
    });

    const tagsWithCounts = tags?.map(tag => ({
      ...tag,
      usage_count: countMap[tag.id] || 0,
    })) || [];

    return { data: tagsWithCounts, error: null };
  } catch (err) {
    console.error('Unexpected error searching secondary tags:', err);
    return { data: null, error: err };
  }
}

/**
 * Get top used secondary tags
 */
export async function listTopSecondaryTags(limit: number = 12): Promise<{ data: SecondaryTag[] | null; error: any }> {
  try {
    // Get all tag usage counts
    const { data: usageCounts, error: usageError } = await supabase
      .from('song_tag_map')
      .select('tag_id');

    if (usageError) {
      console.error('Error fetching tag usage:', usageError);
      return { data: null, error: usageError };
    }

    // Count occurrences
    const countMap: Record<string, number> = {};
    usageCounts?.forEach(row => {
      countMap[row.tag_id] = (countMap[row.tag_id] || 0) + 1;
    });

    // Get top tag IDs
    const topTagIds = Object.entries(countMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit)
      .map(([id]) => id);

    if (topTagIds.length === 0) {
      return { data: [], error: null };
    }

    // Fetch tag details
    const { data: tags, error: tagsError } = await supabase
      .from('song_tags')
      .select('id, name, slug, description')
      .in('id', topTagIds);

    if (tagsError) {
      console.error('Error fetching top tags:', tagsError);
      return { data: null, error: tagsError };
    }

    const tagsWithCounts = tags?.map(tag => ({
      ...tag,
      usage_count: countMap[tag.id] || 0,
    })).sort((a, b) => b.usage_count - a.usage_count) || [];

    return { data: tagsWithCounts, error: null };
  } catch (err) {
    console.error('Unexpected error fetching top secondary tags:', err);
    return { data: null, error: err };
  }
}

/**
 * Get secondary tags for a song
 */
export async function getSongSecondaryTags(songId: string): Promise<{ data: SecondaryTag[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('song_tag_map')
      .select('tag_id, song_tags(id, name, slug, description)')
      .eq('song_id', songId);

    if (error) {
      console.error('Error fetching song secondary tags:', error);
      return { data: null, error };
    }

    const tags = data?.map(row => (row as any).song_tags).filter(Boolean) || [];
    return { data: tags as SecondaryTag[], error: null };
  } catch (err) {
    console.error('Unexpected error fetching song secondary tags:', err);
    return { data: null, error: err };
  }
}

/**
 * Attach a secondary tag to a song
 */
export async function attachSecondaryTag(songId: string, tagId: string): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('song_tag_map')
      .insert({ song_id: songId, tag_id: tagId });

    if (error) {
      console.error('Error attaching secondary tag:', error);
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error attaching secondary tag:', err);
    return { error: err };
  }
}

/**
 * Remove a secondary tag from a song
 */
export async function removeSecondaryTag(songId: string, tagId: string): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('song_tag_map')
      .delete()
      .eq('song_id', songId)
      .eq('tag_id', tagId);

    if (error) {
      console.error('Error removing secondary tag:', error);
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error removing secondary tag:', err);
    return { error: err };
  }
}

/**
 * Create a new secondary tag (admin only)
 */
export async function createSecondaryTag(name: string): Promise<{ data: SecondaryTag | null; error: any }> {
  try {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const { data, error } = await supabase
      .from('song_tags')
      .insert({ name, slug })
      .select('id, name, slug, description')
      .maybeSingle();

    if (error) {
      console.error('Error creating secondary tag:', error);
      return { data: null, error };
    }

    return { data: { ...data, usage_count: 0 } as SecondaryTag, error: null };
  } catch (err) {
    console.error('Unexpected error creating secondary tag:', err);
    return { data: null, error: err };
  }
}

/**
 * Create and attach a secondary tag to a song
 */
export async function createAndAttachSecondaryTag(songId: string, name: string): Promise<{ data: SecondaryTag | null; error: any }> {
  try {
    const { data: tag, error: createError } = await createSecondaryTag(name);

    if (createError || !tag) {
      return { data: null, error: createError };
    }

    const { error: attachError } = await attachSecondaryTag(songId, tag.id);

    if (attachError) {
      return { data: null, error: attachError };
    }

    return { data: tag, error: null };
  } catch (err) {
    console.error('Unexpected error creating and attaching secondary tag:', err);
    return { data: null, error: err };
  }
}

/**
 * Attach multiple secondary tags to a song by name (creates tags if they don't exist)
 */
export async function attachSecondaryTagsByName(songId: string, tagNames: string[]): Promise<{ error: any }> {
  try {
    for (const name of tagNames) {
      const trimmed = name.trim();
      if (!trimmed) continue;

      // Search for existing tag
      const { data: existingTags } = await searchSecondaryTags(trimmed);
      const exactMatch = existingTags?.find(t => t.name.toLowerCase() === trimmed.toLowerCase());

      if (exactMatch) {
        // Use existing tag
        await attachSecondaryTag(songId, exactMatch.id);
      } else {
        // Create new tag
        await createAndAttachSecondaryTag(songId, trimmed);
      }
    }

    return { error: null };
  } catch (err) {
    console.error('Error attaching secondary tags by name:', err);
    return { error: err };
  }
}

// =============================================
// ISSUES/THEMES (song_themes)
// =============================================

export interface Issue {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
}

/**
 * Search issues/themes by name
 */
export async function searchIssues(query: string): Promise<{ data: Issue[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('song_themes')
      .select('id, name, slug, description, color')
      .ilike('name', `%${query}%`)
      .order('name')
      .limit(20);

    if (error) {
      console.error('Error searching issues:', error);
      return { data: null, error };
    }

    return { data: data as Issue[], error: null };
  } catch (err) {
    console.error('Unexpected error searching issues:', err);
    return { data: null, error: err };
  }
}

/**
 * Get issues/themes for a song
 */
export async function getSongIssues(songId: string): Promise<{ data: Issue[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('song_theme_map')
      .select('theme_id, song_themes(id, name, slug, description, color)')
      .eq('song_id', songId);

    if (error) {
      console.error('Error fetching song issues:', error);
      return { data: null, error };
    }

    const issues = data?.map(row => (row as any).song_themes).filter(Boolean) || [];
    return { data: issues as Issue[], error: null };
  } catch (err) {
    console.error('Unexpected error fetching song issues:', err);
    return { data: null, error: err };
  }
}

/**
 * Attach an issue/theme to a song (max 3)
 */
export async function attachIssue(songId: string, themeId: string): Promise<{ error: any }> {
  try {
    // Check current count
    const { data: existing, error: countError } = await supabase
      .from('song_theme_map')
      .select('theme_id')
      .eq('song_id', songId);

    if (countError) {
      console.error('Error checking issue count:', countError);
      return { error: countError };
    }

    if (existing && existing.length >= 3) {
      return { error: new Error('Maximum 3 issues allowed per song') };
    }

    const { error } = await supabase
      .from('song_theme_map')
      .insert({ song_id: songId, theme_id: themeId });

    if (error) {
      console.error('Error attaching issue:', error);
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error attaching issue:', err);
    return { error: err };
  }
}

/**
 * Remove an issue/theme from a song
 */
export async function removeIssue(songId: string, themeId: string): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('song_theme_map')
      .delete()
      .eq('song_id', songId)
      .eq('theme_id', themeId);

    if (error) {
      console.error('Error removing issue:', error);
      return { error };
    }

    return { error: null };
  } catch (err) {
    console.error('Unexpected error removing issue:', err);
    return { error: err };
  }
}
